import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { TrendingUp } from "lucide-react";
import api from "../utils/api";
import { useAuth } from "../contexts/AuthContext";

const ForgotPassword = () => {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (user) {
    return <Navigate to="/productivity-coaching" replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setStatusMessage("");
    setErrorMessage("");

    try {
      await api.post("/auth/forgot-password", { email });
      setStatusMessage(
        "If an account exists with that email, we've sent instructions to reset your password."
      );
      setEmail("");
    } catch (error) {
      console.error("Forgot password error:", error);
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to send reset email. Please try again.";
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{
        background:
          "linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-secondary) 100%)",
      }}
    >
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="brand-card p-8 brand-shadow-lg">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 brand-gradient rounded-2xl flex items-center justify-center brand-shadow-lg">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="hellopc-wordmark-large text-center mb-2 hellopc-logo">
              <span className="hello">Hello</span>
              <span className="pc">PC</span>
            </h1>
            <h2 className="text-lg font-semibold text-slate-700">
              Reset your password
            </h2>
            <p className="text-slate-500 mt-2">
              Enter the email associated with your account and we'll send you a reset link.
            </p>
          </div>

          {statusMessage && (
            <div className="rounded-lg bg-green-50 border border-green-200 p-4 mb-6">
              <p className="text-sm text-green-700">{statusMessage}</p>
            </div>
          )}

          {errorMessage && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 mb-6">
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="forgot-password-email"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                Email Address
              </label>
              <input
                id="forgot-password-email"
                name="email"
                type="email"
                required
                className="block w-full px-4 py-3 border border-slate-300 placeholder-slate-400 text-slate-900 rounded-lg brand-focus hover:border-slate-400 brand-transition"
                placeholder="coach@example.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 px-4 brand-gradient text-white font-semibold rounded-lg brand-transition brand-hover-lift brand-focus disabled:opacity-50 disabled:transform-none"
            >
              {isSubmitting ? "Sending..." : "Send reset email"}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-500">
            <Link to="/login" className="text-brand-primary font-medium">
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
